import React, { useState, useEffect, useRef } from 'react';
import { createDeal, updateDeal, getClients } from '../utils/api';
import { parseDealText, formatParsedDeal } from '../utils/dealParser';
import { ar, en } from '../utils/translations';
import { convertArabicNumerals } from '../utils/numberUtils';
import DualCalendarPicker from './DualCalendarPicker';
import ClientModal from './ClientModal';
// React Icons
import { 
  FaMicrophone, 
  FaStop, 
  FaUser, 
  FaSearch,
  FaFileAlt,
  FaEdit,
  FaBuilding,
  FaHome,
  FaMapMarkerAlt,
  FaStore,
  FaLandmark,
  FaPlus,
  FaMoneyBillWave,
  FaKey,
  FaCheckCircle,
  FaClock,
  FaChevronDown,
  FaChevronRight
} from 'react-icons/fa';

const DealModal = ({ deal, onClose, language = 'ar', contactType, contactId }) => {
  const t = language === 'ar' ? ar : en;
  const isRTL = language === 'ar';
  
  const [formData, setFormData] = useState({
    contact_type: 'client', // Always client now (leads unified)
    contact_id: contactId || '',
    property_type: '',
    property_address: '',
    deal_type: 'sale',
    price: '',
    deal_date: '',
    due_date: '', // For priority calculation
    progress: 0,
    status: 'pending',
    priority_level: 'medium', // Priority level
    priority_score: 50, // Priority score
    notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [voiceText, setVoiceText] = useState('');
  const [isProcessingVoice, setIsProcessingVoice] = useState(false);
  
  // Text input state
  const [textInput, setTextInput] = useState('');
  const [showTextInput, setShowTextInput] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  
  // Form sections
  const [expandedSections, setExpandedSections] = useState({
    input: true,
    contact: true,
    details: false,
    additional: false
  });

  const recognitionRef = useRef(null);

  useEffect(() => {
    // If deal has isQuickAdd flag, it means it came from quick input
    if (deal && deal.isQuickAdd) {
      setFormData(prev => ({
        ...prev,
        ...deal,
        contact_type: 'client', // Always client now
        contact_id: deal.contact_id || prev.contact_id
      }));
      setExpandedSections({ input: false, contact: true, details: true, additional: true });
      setTextInput(deal.notes || '');
    }
    
    // Initialize Web Speech API
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      
      recognitionInstance.continuous = false;
      recognitionInstance.interimResults = false;
      recognitionInstance.lang = language === 'ar' ? 'ar-SA' : 'en-US';
      
      recognitionInstance.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join(' ');
        setVoiceText(prev => prev + ' ' + transcript);
        setIsProcessingVoice(true);
        
        // Parse the voice input
        setTimeout(() => {
          const parsed = parseDealText(transcript);
          applyParsedData(parsed);
          setIsProcessingVoice(false);
        }, 500);
      };
      
      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);
        if (event.error === 'no-speech') {
          alert(language === 'ar' ? 'لم يتم اكتشاف أي كلام. حاول مرة أخرى.' : 'No speech detected. Please try again.');
        } else if (event.error === 'not-allowed') {
          alert(language === 'ar' ? 'يجب السماح بالوصول إلى الميكروفون.' : 'Microphone access is required.');
        }
      };
      
      recognitionInstance.onend = () => {
        setIsRecording(false);
      };
      
      setRecognition(recognitionInstance);
      recognitionRef.current = recognitionInstance;
    }
    
    loadContacts();
    
    if (deal) {
      // Calculate priority if not already set
      const priority = deal.priority_level ? 
        { level: deal.priority_level, score: deal.priority_score || 50 } :
        calculatePriority(deal.price, deal.due_date, deal.deal_date, deal.progress || 0);
      
      setFormData({
        contact_type: 'client', // Always client now (unified)
        contact_id: deal.contact_id || '',
        property_type: deal.property_type || '',
        property_address: deal.property_address || '',
        deal_type: deal.deal_type || 'sale',
        price: deal.price || '',
        deal_date: deal.deal_date || '',
        due_date: deal.due_date || '', // Priority due date
        progress: deal.progress || 0,
        status: deal.status || 'pending',
        priority_level: priority.level,
        priority_score: priority.score,
        notes: deal.notes || ''
      });
      setExpandedSections({ input: false, contact: true, details: true, additional: true });
    }
  }, [deal, contactType, contactId, language]);

  const loadContacts = async (selectNewContactId = null) => {
    try {
      setLoadingContacts(true);
      const clientsRes = await getClients('', null);
      
      const allContacts = (clientsRes.data || []).map(c => ({ ...c, type: 'client', displayName: c.name }));
      
      setContacts(allContacts);
      
      // If a new contact was created, select it
      if (selectNewContactId) {
        setFormData(prev => ({
          ...prev,
          contact_type: 'client', // Always client now
          contact_id: selectNewContactId.toString()
        }));
      }
      
      // Return contacts for use in callbacks
      return allContacts;
    } catch (error) {
      console.error('Error loading contacts:', error);
      return [];
    } finally {
      setLoadingContacts(false);
    }
  };

  const applyParsedData = (parsed) => {
    if (parsed.hasData || parsed.property_type || parsed.price || parsed.property_address) {
      setFormData(prev => ({
        ...prev,
        property_type: parsed.property_type || prev.property_type,
        property_address: parsed.property_address || prev.property_address,
        deal_type: parsed.deal_type || prev.deal_type,
        price: parsed.price || prev.price,
        deal_date: parsed.deal_date || prev.deal_date,
        status: parsed.status || prev.status,
        notes: parsed.notes || prev.notes
      }));
      setParsedData(parsed);
      setExpandedSections(prev => ({ ...prev, details: true }));
    }
  };

  const handleTextInput = (text) => {
    setTextInput(text);
    if (text.trim().length > 10) {
      const parsed = parseDealText(text);
      const formatted = formatParsedDeal(parsed);
      setParsedData(formatted);
      
      if (formatted.hasData) {
        applyParsedData(parsed);
      }
    }
  };

  const handleStartRecording = () => {
    if (!recognitionRef.current) {
      alert(language === 'ar' ? 'ميزة التعرف على الصوت غير متاحة في هذا المتصفح.' : 'Speech recognition is not available in this browser.');
      return;
    }
    
    try {
      setVoiceText('');
      setIsRecording(true);
      recognitionRef.current.start();
    } catch (error) {
      console.error('Error starting recognition:', error);
      setIsRecording(false);
    }
  };

  const handleStopRecording = () => {
    if (recognitionRef.current && isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    }
  };

  // Calculate priority based on deal data
  const calculatePriority = (price, dueDate, dealDate, progress = 0) => {
    let score = 0;
    
    // Value score (40% weight) - normalize price to 0-100
    let valueScore = 50; // Default
    if (price) {
      const priceNum = parseFloat(price.toString().replace(/[^0-9.]/g, '')) || 0;
      valueScore = Math.min(100, (priceNum / 10000) * 10); // 100k = 100 points
    }
    score += valueScore * 0.4;
    
    // Urgency score (40% weight) - based on due_date or deal_date
    const targetDate = dueDate || dealDate;
    let urgencyScore = 50;
    if (targetDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const due = new Date(targetDate);
      due.setHours(0, 0, 0, 0);
      const diffTime = due - today;
      const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (daysUntil < 0) urgencyScore = 100; // Overdue
      else if (daysUntil === 0) urgencyScore = 90; // Due today
      else if (daysUntil <= 3) urgencyScore = 80; // Due in 3 days
      else if (daysUntil <= 7) urgencyScore = 60; // Due in a week
      else urgencyScore = 40; // More than a week
    }
    score += urgencyScore * 0.4;
    
    // Progress score (20% weight)
    score += (progress || 0) * 0.2;
    
    // Determine level
    let level = 'medium';
    if (score >= 80) level = 'critical';
    else if (score >= 60) level = 'high';
    else if (score >= 40) level = 'medium';
    else level = 'low';
    
    return { level, score: Math.round(score) };
  };

  const handleChange = (e) => {
    let value = e.target.value;
    if (e.target.name === 'price') {
      value = convertArabicNumerals(value);
    }
    if (e.target.name === 'progress') {
      const parsed = value === '' ? '' : Number(value);
      value = Number.isNaN(parsed) ? 0 : Math.max(0, Math.min(100, parsed));
    }
    
    const updatedData = {
      ...formData,
      [e.target.name]: value
    };
    
    // Recalculate priority when price, due_date, or deal_date changes
    if (e.target.name === 'price' || e.target.name === 'due_date' || e.target.name === 'deal_date' || e.target.name === 'progress') {
      const priority = calculatePriority(
        updatedData.price,
        updatedData.due_date,
        updatedData.deal_date,
        updatedData.progress || 0
      );
      updatedData.priority_level = priority.level;
      updatedData.priority_score = priority.score;
    }
    
    setFormData(updatedData);
  };

  const handlePropertyTypeChange = (type) => {
    setFormData({
      ...formData,
      property_type: type
    });
  };

  const handleDealTypeChange = (type) => {
    setFormData({
      ...formData,
      deal_type: type
    });
  };

  const handleStatusChange = (status) => {
    const nextProgress = status === 'completed' ? 100 : status === 'pending' ? 0 : formData.progress;
    const priority = calculatePriority(
      formData.price,
      formData.due_date,
      formData.deal_date,
      nextProgress || 0
    );
    setFormData({
      ...formData,
      status,
      progress: nextProgress,
      priority_level: priority.level,
      priority_score: priority.score
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.contact_id) {
      const message = language === 'ar' 
        ? 'يرجى اختيار جهة الاتصال أو إضافة عميل/فرصة جديدة قبل الحفظ.\n\nيمكنك:\n1. اختيار عميل/فرصة موجودة من القائمة\n2. النقر على "إضافة عميل جديد" أو "إضافة فرصة جديدة"'
        : 'Please select a contact or add a new client/lead before saving.\n\nYou can:\n1. Select an existing client/lead from the list\n2. Click "Add New Client" or "Add New Lead"';
      alert(message);
      // Scroll to contact section and expand it
      setExpandedSections(prev => ({ ...prev, contact: true }));
      return;
    }
    
    try {
      setLoading(true);
      
      // Validate and prepare data
      if (!formData.contact_id || formData.contact_id === 'undefined' || formData.contact_id === '') {
        alert(language === 'ar' 
          ? 'يرجى اختيار عميل أو إضافة عميل جديد قبل الحفظ.'
          : 'Please select a client or add a new client before saving.');
        setExpandedSections(prev => ({ ...prev, contact: true }));
        setLoading(false);
        return;
      }
      
      // Ensure contact_id is a valid number
      const contactIdInt = parseInt(formData.contact_id, 10);
      
      if (isNaN(contactIdInt) || contactIdInt <= 0) {
        alert(language === 'ar' 
          ? 'خطأ في معرف جهة الاتصال. يرجى المحاولة مرة أخرى.'
          : 'Invalid contact ID. Please try again.');
        setLoading(false);
        return;
      }
      
      // Calculate priority before saving
      const priority = calculatePriority(
        formData.price,
        formData.due_date,
        formData.deal_date,
        deal?.progress || 0
      );
      
      // Prepare clean data object - only send defined fields
      const dealData = {
        contact_type: 'client', // Always client now (unified)
        contact_id: contactIdInt,
        property_type: formData.property_type || null,
        property_address: formData.property_address || null,
        deal_type: formData.deal_type || 'sale',
        price: formData.price || null,
        deal_date: formData.deal_date || null,
        due_date: formData.due_date || null, // Priority due date
        progress: formData.progress !== '' ? Number(formData.progress) : 0,
        status: formData.status || 'pending',
        priority_level: priority.level,
        priority_score: priority.score,
        notes: formData.notes || null
      };
      
      console.log('Saving deal with data:', dealData);
      console.log('About to call createDeal/updateDeal...');
      console.log('Deal object:', deal);
      console.log('Deal ID:', deal?.id);
      
      // Check if this is a new deal (no deal object, or deal object without id)
      if (deal && deal.id) {
        console.log('Calling updateDeal for deal ID:', deal.id);
        await updateDeal(deal.id, dealData);
        console.log('updateDeal completed successfully');
      } else {
        console.log('Calling createDeal (new deal)...');
        const result = await createDeal(dealData);
        console.log('createDeal completed successfully, result:', result);
      }
      console.log('Calling onClose()...');
      onClose();
    } catch (error) {
      console.error('Error saving deal:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error debug info:', error.response?.data?.debug);
      const errorMessage = error.response?.data?.error || error.message || (t.failedToSaveDeal || 'Failed to save deal');
      const debugInfo = error.response?.data?.debug;
      if (debugInfo) {
        console.error('Parameter types:', debugInfo.paramTypes);
      }
      alert(errorMessage + (debugInfo ? '\n\nCheck console for debug info' : ''));
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ 
        maxWidth: '900px', 
        maxHeight: '95vh', 
        overflowY: 'auto',
        direction: isRTL ? 'rtl' : 'ltr'
      }}>
        <div className="modal-header">
          <h2>{deal ? (t.editDeal || 'Edit Deal') : (t.addDeal || 'Add New Deal')}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
          {/* Voice/Text Input Section */}
          <div style={{ 
            marginBottom: '20px', 
            padding: '20px', 
            background: '#fff4e6', 
            borderRadius: '12px',
            border: '2px solid #ffd4a3'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '15px',
              cursor: 'pointer'
            }} onClick={() => toggleSection('input')}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaMicrophone style={{ color: '#f97316', fontSize: '18px' }} />
                {language === 'ar' ? 'أدخل الصفقة بالصوت أو النص' : 'Enter Deal by Voice or Text'}
              </h3>
              {expandedSections.input ? (
                <FaChevronDown style={{ color: '#666', fontSize: '14px' }} />
              ) : (
                <FaChevronRight style={{ color: '#666', fontSize: '14px' }} />
              )}
            </div>

            {expandedSections.input && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {/* Voice Recording */}
                <div style={{ 
                  padding: '15px', 
                  background: '#ffffff', 
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0'
                }}>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                    <button
                      type="button"
                      onClick={isRecording ? handleStopRecording : handleStartRecording}
                      style={{
                        padding: '12px 24px',
                        background: isRecording ? '#ef4444' : '#10b981',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.3s ease'
                      }}
                      disabled={!recognitionRef.current}
                    >
                      {isRecording ? (
                        <>
                          <FaStop style={{ fontSize: '16px' }} />
                          <span>{language === 'ar' ? 'إيقاف التسجيل' : 'Stop Recording'}</span>
                        </>
                      ) : (
                        <>
                          <FaMicrophone style={{ fontSize: '16px' }} />
                          <span>{language === 'ar' ? 'ابدأ التسجيل الصوتي' : 'Start Voice Recording'}</span>
                        </>
                      )}
                    </button>
                    {isProcessingVoice && (
                      <span style={{ color: '#666', fontSize: '0.9rem' }}>
                        {language === 'ar' ? 'جاري المعالجة...' : 'Processing...'}
                      </span>
                    )}
                  </div>
                  
                  {voiceText && (
                    <div style={{ 
                      marginTop: '10px', 
                      padding: '10px', 
                      background: '#f9fafb', 
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      color: '#333',
                      minHeight: '40px'
                    }}>
                      {voiceText}
                    </div>
                  )}

                  {!recognitionRef.current && (
                    <p style={{ marginTop: '10px', fontSize: '0.85rem', color: '#666' }}>
                      {language === 'ar' 
                        ? '⚠️ التعرف على الصوت غير متاح. استخدم إدخال النص بدلاً من ذلك.' 
                        : '⚠️ Speech recognition not available. Use text input instead.'}
                    </p>
                  )}
                </div>

                {/* Text Input */}
                <div style={{ 
                  padding: '15px', 
                  background: '#ffffff', 
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0'
                }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    {language === 'ar' ? 'أو اكتب وصف الصفقة' : 'Or Type Deal Description'}
                  </label>
                  <textarea
                    value={textInput}
                    onChange={(e) => handleTextInput(e.target.value)}
                    placeholder={language === 'ar' 
                      ? 'مثال: بيع شقة في الرياض بسعر 500,000 ريال بتاريخ اليوم' 
                      : 'Example: Sold apartment in Riyadh for 500,000 SAR today'}
                    style={{
                      width: '100%',
                      minHeight: '100px',
                      padding: '12px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                  {parsedData && parsedData.hasData && (
                    <div style={{ 
                      marginTop: '10px', 
                      padding: '10px', 
                      background: '#d1fae5', 
                      borderRadius: '6px',
                      fontSize: '0.85rem'
                    }}>
                      ✓ {language === 'ar' ? 'تم استخراج المعلومات تلقائياً' : 'Information extracted automatically'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Contact Selection */}
          <div style={{ 
            marginBottom: '20px', 
            padding: '20px', 
            background: '#f9fafb', 
            borderRadius: '12px'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '15px',
              cursor: 'pointer'
            }} onClick={() => toggleSection('contact')}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaUser style={{ color: '#f97316', fontSize: '18px' }} />
                {t.contact || 'Contact'}
              </h3>
              {expandedSections.contact ? (
                <FaChevronDown style={{ color: '#666', fontSize: '14px' }} />
              ) : (
                <FaChevronRight style={{ color: '#666', fontSize: '14px' }} />
              )}
            </div>

            {expandedSections.contact && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Contact type is always 'client' now (unified) - no need to show selector */}

                <div className="form-group">
                  <label>{t.selectContact || 'Select Contact'}</label>
                  
                  {/* Warning message when contact is missing (especially for quick add) */}
                  {!formData.contact_id && deal && deal.isQuickAdd && (
                    <div style={{
                      padding: '12px 16px',
                      marginBottom: '12px',
                      background: '#fef3c7',
                      border: '2px solid #f59e0b',
                      borderRadius: '8px',
                      color: '#92400e'
                    }}>
                      <strong style={{ display: 'block', marginBottom: '4px' }}>
                        {language === 'ar' ? '⚠️ لم يتم تحديد جهة الاتصال' : '⚠️ Contact Not Selected'}
                      </strong>
                      <div style={{ fontSize: '0.9rem' }}>
                        {language === 'ar' 
                          ? 'يرجى اختيار عميل موجود أو إضافة عميل جديد' 
                          : 'Please select an existing client or add a new client'}
                      </div>
                    </div>
                  )}
                  
                  {loadingContacts ? (
                    <p>{t.loading}</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <select
                        name="contact_id"
                        value={formData.contact_id}
                        onChange={handleChange}
                        required
                        style={{
                          border: !formData.contact_id && deal && deal.isQuickAdd ? '2px solid #f59e0b' : '1px solid #e0e0e0'
                        }}
                      >
                        <option value="">{t.selectContact || 'Select Contact'}</option>
                        {contacts
                          .filter(c => c.type === 'client') // Always client now
                          .map(contact => (
                            <option key={`${contact.type}-${contact.id}`} value={contact.id}>
                              {contact.displayName || contact.name}
                            </option>
                          ))}
                      </select>
                      
                      {/* Add New Client Button */}
                      <div style={{ display: 'flex', gap: '10px' }}>
                          <button
                            type="button"
                            onClick={() => setShowClientModal(true)}
                            style={{
                              flex: 1,
                              padding: '10px 16px',
                            background: '#f97316',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '8px',
                              fontSize: '0.9rem',
                              fontWeight: '500',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#ea580c';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#f97316';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            <FaPlus style={{ fontSize: '14px' }} />
                            <span>{language === 'ar' ? 'إضافة عميل جديد' : 'Add New Client'}</span>
                          </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Deal Details */}
          <div style={{ 
            marginBottom: '20px', 
            padding: '20px', 
            background: '#f9fafb', 
            borderRadius: '12px'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '15px',
              cursor: 'pointer'
            }} onClick={() => toggleSection('details')}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaFileAlt style={{ color: '#f97316', fontSize: '18px' }} />
                {t.dealDetails || 'Deal Details'}
              </h3>
              {expandedSections.details ? (
                <FaChevronDown style={{ color: '#666', fontSize: '14px' }} />
              ) : (
                <FaChevronRight style={{ color: '#666', fontSize: '14px' }} />
              )}
            </div>

            {expandedSections.details && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Property Type - Icon Grid */}
                <div className="form-group">
                  <label style={{ marginBottom: '10px', display: 'block', fontWeight: '500' }}>{t.propertyType || 'Property Type'}</label>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                    gap: '10px'
                  }}>
                    {[
                      { value: 'apartment', icon: FaBuilding, label: t.apartment || 'Apartment', color: '#3b82f6' },
                      { value: 'villa', icon: FaHome, label: t.villa || 'Villa', color: '#10b981' },
                      { value: 'land', icon: FaMapMarkerAlt, label: t.land || 'Land', color: '#f59e0b' },
                      { value: 'commercial', icon: FaStore, label: t.commercial || 'Commercial', color: '#ef4444' },
                      { value: 'office', icon: FaLandmark, label: t.office || 'Office', color: '#8b5cf6' },
                      { value: 'other', icon: FaPlus, label: t.other || 'Other', color: '#6b7280' }
                    ].map((prop) => {
                      const Icon = prop.icon;
                      const isSelected = formData.property_type === prop.value;
                      return (
                        <button
                          key={prop.value}
                          type="button"
                          onClick={() => handlePropertyTypeChange(prop.value)}
                          style={{
                            padding: '12px 8px',
                            border: isSelected ? `2px solid ${prop.color}` : '1.5px solid #e0e0e0',
                            borderRadius: '8px',
                            background: isSelected ? `${prop.color}15` : '#fff',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: isSelected ? '600' : '400',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '6px',
                            minHeight: '75px',
                            justifyContent: 'center',
                            color: isSelected ? prop.color : '#4b5563'
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.background = '#f9fafb';
                              e.currentTarget.style.borderColor = prop.color;
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.background = '#fff';
                              e.currentTarget.style.borderColor = '#e0e0e0';
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }
                          }}
                        >
                          <Icon style={{ fontSize: '24px' }} />
                          <span>{prop.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Deal Type - Icon Buttons */}
                <div className="form-group">
                  <label style={{ marginBottom: '10px', display: 'block', fontWeight: '500' }}>{t.dealType || 'Deal Type'}</label>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    {[
                      { value: 'sale', icon: FaMoneyBillWave, label: t.sale || 'Sale', color: '#10b981' },
                      { value: 'rent', icon: FaKey, label: t.rent || 'Rent', color: '#3b82f6' }
                    ].map((type) => {
                      const Icon = type.icon;
                      const isSelected = formData.deal_type === type.value;
                      return (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => handleDealTypeChange(type.value)}
                          style={{
                            flex: 1,
                            padding: '12px 16px',
                            border: isSelected ? `2px solid ${type.color}` : '1px solid #e0e0e0',
                            borderRadius: '8px',
                            background: isSelected ? `${type.color}15` : '#fff',
                            cursor: 'pointer',
                            fontSize: '0.95rem',
                            fontWeight: isSelected ? '600' : '400',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            color: isSelected ? type.color : '#4b5563'
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.background = '#f9fafb';
                              e.currentTarget.style.borderColor = type.color;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.background = '#fff';
                              e.currentTarget.style.borderColor = '#e0e0e0';
                            }
                          }}
                        >
                          <Icon style={{ fontSize: '18px' }} />
                          <span>{type.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Status - Icon Buttons */}
                <div className="form-group">
                  <label style={{ marginBottom: '10px', display: 'block', fontWeight: '500' }}>{t.status || 'Status'}</label>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    {[
                      { value: 'pending', icon: FaClock, label: t.pending || 'Pending', color: '#f59e0b' },
                      { value: 'completed', icon: FaCheckCircle, label: t.completed || 'Completed', color: '#10b981' }
                    ].map((status) => {
                      const Icon = status.icon;
                      const isSelected = formData.status === status.value;
                      return (
                        <button
                          key={status.value}
                          type="button"
                          onClick={() => handleStatusChange(status.value)}
                          style={{
                            flex: 1,
                            padding: '12px 16px',
                            border: isSelected ? `2px solid ${status.color}` : '1px solid #e0e0e0',
                            borderRadius: '8px',
                            background: isSelected ? `${status.color}15` : '#fff',
                            cursor: 'pointer',
                            fontSize: '0.95rem',
                            fontWeight: isSelected ? '600' : '400',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            color: isSelected ? status.color : '#4b5563'
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.background = '#f9fafb';
                              e.currentTarget.style.borderColor = status.color;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.background = '#fff';
                              e.currentTarget.style.borderColor = '#e0e0e0';
                            }
                          }}
                        >
                          <Icon style={{ fontSize: '18px' }} />
                          <span>{status.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Price, Deal Date, and Due Date in Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                  <div className="form-group">
                    <label>{t.price || 'Price'}</label>
                    <input
                      type="text"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      placeholder={t.pricePlaceholder || 'e.g., 500,000 SAR'}
                    />
                  </div>

                  <div className="form-group">
                    <label>{t.dealDate || 'Deal Date'}</label>
                    <DualCalendarPicker
                      value={formData.deal_date}
                      onChange={(v) => setFormData((prev) => ({ ...prev, deal_date: v }))}
                      language={language}
                      placeholder={language === 'ar' ? 'اختر تاريخ الصفقة' : 'Pick deal date'}
                    />
                  </div>

                  <div className="form-group">
                    <label>{language === 'ar' ? 'الموعد النهائي (للأولوية)' : 'Due Date (for Priority)'}</label>
                    <DualCalendarPicker
                      value={formData.due_date}
                      onChange={(v) => setFormData((prev) => ({ ...prev, due_date: v }))}
                      language={language}
                      placeholder={language === 'ar' ? 'اختر الموعد النهائي' : 'Pick due date'}
                    />
                </div>

                  <div className="form-group">
                    <label>{t.progress || (language === 'ar' ? 'نسبة التقدم %' : 'Progress %')}</label>
                    <input
                      type="number"
                      name="progress"
                      min="0"
                      max="100"
                      value={formData.progress}
                      onChange={handleChange}
                    />
                </div>
                </div>

                {/* Priority Indicator */}
                {formData.price && (formData.due_date || formData.deal_date) && (
                  <div style={{
                    marginTop: '15px',
                    padding: '12px',
                    background: '#f9fafb',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <span style={{ fontWeight: '600', fontSize: '0.9rem' }}>
                        {language === 'ar' ? 'مستوى الأولوية:' : 'Priority Level:'}
                      </span>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        background: formData.priority_level === 'critical' ? '#fee2e2' :
                                   formData.priority_level === 'high' ? '#fff7ed' :
                                   formData.priority_level === 'medium' ? '#fefce8' : '#f1f5f9',
                        color: formData.priority_level === 'critical' ? '#991b1b' :
                              formData.priority_level === 'high' ? '#9a3412' :
                              formData.priority_level === 'medium' ? '#854d0e' : '#475569',
                        border: `1px solid ${formData.priority_level === 'critical' ? '#ef4444' :
                                 formData.priority_level === 'high' ? '#f97316' :
                                 formData.priority_level === 'medium' ? '#eab308' : '#94a3b8'}`
                      }}>
                        {formData.priority_level === 'critical' ? (language === 'ar' ? '🔴 حرج' : '🔴 Critical') :
                         formData.priority_level === 'high' ? (language === 'ar' ? '🟠 عالي' : '🟠 High') :
                         formData.priority_level === 'medium' ? (language === 'ar' ? '🟡 متوسط' : '🟡 Medium') :
                         (language === 'ar' ? '⚪ منخفض' : '⚪ Low')}
                      </span>
                      {formData.priority_score && (
                        <span style={{ fontSize: '0.85rem', opacity: 0.7 }}>
                          (Score: {formData.priority_score})
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.8rem', opacity: 0.6 }}>
                      {language === 'ar' 
                        ? 'يتم حساب الأولوية تلقائياً بناءً على السعر والموعد النهائي والتقدم'
                        : 'Priority is calculated automatically based on price, due date, and progress'}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Additional Info */}
          <div style={{ 
            marginBottom: '20px', 
            padding: '20px', 
            background: '#f9fafb', 
            borderRadius: '12px'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '15px',
              cursor: 'pointer'
            }} onClick={() => toggleSection('additional')}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaEdit style={{ color: '#f97316', fontSize: '18px' }} />
                {t.additionalInfo || 'Additional Information'}
              </h3>
              {expandedSections.additional ? (
                <FaChevronDown style={{ color: '#666', fontSize: '14px' }} />
              ) : (
                <FaChevronRight style={{ color: '#666', fontSize: '14px' }} />
              )}
            </div>

            {expandedSections.additional && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div className="form-group">
                  <label>{t.propertyAddress || 'Property Address'}</label>
                  <input
                    type="text"
                    name="property_address"
                    value={formData.property_address}
                    onChange={handleChange}
                    placeholder={t.propertyAddressPlaceholder || 'Enter property address'}
                  />
                </div>

                <div className="form-group">
                  <label>{t.notes || 'Notes'}</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows="4"
                    placeholder={t.dealNotesPlaceholder || 'Additional notes about this deal...'}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="form-actions" style={{ 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'flex-end',
            paddingTop: '20px',
            borderTop: '1px solid #e0e0e0'
          }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {t.cancel || 'Cancel'}
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (t.saving || 'Saving...') : (t.save || 'Save')}
            </button>
          </div>
        </form>
      </div>
      
      {/* Client Modal */}
      {showClientModal && (
        <ClientModal
          client={null}
          language={language}
          onClose={async () => {
            setShowClientModal(false);
            // Reload contacts list and select the most recently created client
            const updatedContacts = await loadContacts();
            if (updatedContacts && updatedContacts.length > 0) {
              // Find the most recent client (sort by id DESC to get newest first)
              const clients = updatedContacts.filter(c => c.type === 'client');
              if (clients.length > 0) {
                // Sort by id descending (highest ID = newest)
                clients.sort((a, b) => b.id - a.id);
                const latestClient = clients[0];
                setFormData(prev => ({
                  ...prev,
                  contact_type: 'client',
                  contact_id: latestClient.id.toString()
                }));
              }
            }
          }}
        />
      )}
      
    </div>
  );
};

export default DealModal;

